import { Text, BasicButton, Stack } from "@certn/cascadia";
import { Card, Icon, TextArea, FormLabelRAC } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { AIMessageGeneratorFormikFieldProps } from "./types";
import { useAIMessageGenerator } from "./useAIMessageGenerator";
interface AIMessageGeneratorProps extends AIMessageGeneratorFormikFieldProps {
  isRequired?: boolean;
}

export function AIMessageGenerator({
  onChange,
  errors,
  isInvalid,
  value,
  name,
  label,
  testID,
  isRequired,
  description,
}: AIMessageGeneratorProps) {
  const { t } = useTranslation();
  const { message, isSubmitting, generateMessage, setMessage } =
    useAIMessageGenerator({
      onMessageGenerated: onChange,
      value,
    });

  return (
    <Stack gap="space-1" data-testid={testID}>
      <FormLabelRAC isRequired={isRequired}>{label}</FormLabelRAC>
      <Card hideBorder>
        <Stack
          orientation="horizontal"
          className={styles.heading}
          justify="space-between"
        >
          <Stack orientation="horizontal" gap="space-1">
            <Icon
              name="sparkles"
              type="custom"
              color="background-base"
              aria-hidden="true"
            />
            <Text style="global/label-small">
              {t("Generate your message with AI")}
            </Text>
          </Stack>
          <BasicButton
            leftIcon="ai_gen_text"
            onPress={generateMessage}
            size="small"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            aria-label={t("Generate message")}
          >
            {value ? t("Improve message") : t("Generate message")}
          </BasicButton>
        </Stack>
        <div className={styles.messageContainer}>
          <TextArea
            className={styles.messageInput}
            isInvalid={isInvalid}
            data-testid={testID}
            errors={errors}
            value={isSubmitting ? t("Generating message...") : message}
            onChange={setMessage}
            name={name}
            testID="enterApplicantsPageAdditionalMessageInput"
            resize="none"
            aria-label={t("Generated message")}
            placeholder={t("Your AI-generated message will appear here")}
            isDisabled={isSubmitting}
          />
        </div>
      </Card>
      <Text style="input/helper" color="text-weak">
        {description}
      </Text>
    </Stack>
  );
}

const styles = {
  cardContainer: css({
    padding: "var(--space-4)",
  }),
  heading: css({
    borderTopRightRadius: "var(--border-radius-3)",
    borderTopLeftRadius: "var(--border-radius-3)",
    borderTop: "1px solid var(--border-success-hover)",
    borderLeft: "1px solid var(--border-success-hover)",
    borderRight: "1px solid var(--border-success-hover)",
    borderBottom: "1px solid var(--border-success-hover)",
    padding: "var(--space-2)",
    background:
      "linear-gradient(90deg, rgba(27, 183, 147, 0.05) 0%, rgba(12, 81, 65, 0.05) 100%);",
  }),
  messageContainer: css({
    "& > div": {
      borderBottomRightRadius: "var(--border-radius-2)",
      borderBottomLeftRadius: "var(--border-radius-2)",
      borderColor: "var(--border-input)",
      boxShadow: "none",
      borderWidth: "1px",
      borderStyle: "solid",
      borderTop: "none",
    },
    "& textarea": {
      height: "88px",
      boxShadow: "none",
      border: "none",
    },
  }),
  messageInput: css({
    border: "none",
  }),
};
